import { memo, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslate } from "../../../context/TranslationContext";
import { useUpdateUserMutation } from "../../../redux/Apis/User";
import {
  convertPrice,
  formatCurrency,
  getConfigByCountryCode,
} from "../../../utils/localization";
import Button from "../../button";
import SwitchToBothRoleModal from "../../modal/SwitchToBothRoleModal";

const MembershipCard = (props) => {
  const { t } = useTranslate("membership");
  const lang = useSelector((state) => state.language.lang);
  const { isLoggedIn, user } = useSelector((state) => state.authSlice);
  const { data, onSubscriptionBuy, index, selectedCountry } = props;
  const navigate = useNavigate();
  const [convertedPrice, setConvertedPrice] = useState(0);
  const [showRoleSwitchModal, setShowRoleSwitchModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const role = user?.role;
  const isCreator = role === "creator";
  const isBoth = role === "both";
  const isLearner = role === "learner";
  const isFreePlan = index === 0 && data.price === 0;
  const title = isFreePlan ? t("freeTitle") : data.title;
  const priceText = isFreePlan
    ? t("freePrice")
    : formatCurrency(convertedPrice, selectedCountry);
  const isFitEnterprise = data.title === "Fit Enterprise";
  const buttonText = isFreePlan
    ? t("becomeCreator")
    : isFitEnterprise
      ? t("contactUs")
      : data?.button || t("buyNow");
  const isDisabled = isFreePlan && (isCreator || isBoth);
  const finalButtonText = isDisabled ? t("alreadyActive") : buttonText;
  const { currency } = getConfigByCountryCode(selectedCountry);
  const [updateUser] = useUpdateUserMutation();

  useEffect(() => {
    if (!isFreePlan) {
      try {
        const targetCurrency = currency || "GBP";
        const result = convertPrice(data.price, "GBP", targetCurrency);

        if (result && typeof result.then === "function") {
          result
            .then(setConvertedPrice)
            .catch(() => setConvertedPrice(data.price));
        } else {
          setConvertedPrice(result);
        }
      } catch (error) {
        console.error("Price conversion failed:", error);
        setConvertedPrice(data.price);
      }
    }
  }, [data.price, currency, isFreePlan]);

  const handleClick = async () => {
    setIsButtonLoading(true);

    try {
      if (isFreePlan) {
        if (!isLoggedIn) {
          navigate("/auth/signup");
          return;
        }

        if (isLearner) {
          setShowRoleSwitchModal(true);
          return;
        }

        return;
      }

      if (isFitEnterprise) {
        navigate("/contact");
        return;
      }

      if (isLearner) {
        setPendingAction(() => async () => {
          try {
            if (data?.route) {
              navigate(data.route);
            } else {
              await onSubscriptionBuy(data?._id);
            }
          } catch (err) {
            toast.error(err?.data?.message || t("somethingWentWrong"));
          }
        });

        setShowRoleSwitchModal(true);
        return;
      }

      if (data?.route) {
        navigate(data.route);
      } else {
        await onSubscriptionBuy(data?._id);
      }
    } finally {
      setIsButtonLoading(false);
    }
  };

  const handleSwitchRole = async () => {
    setIsLoadingUpdate(true);

    try {
      const payload = { role: "both" };

      await updateUser({
        id: user?._id,
        payload,
        params: { lang },
      }).unwrap();

      toast.success(t("roleUpdateSuccess"));

      setShowRoleSwitchModal(false);

      if (pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.message || t("roleUpdateError"));
    } finally {
      setIsLoadingUpdate(false);
    }
  };

  return (
    <>
      <div className="mx-1 p-4 h-100 w-100 membership-card position-relative">
        {/* <div className="d-flex flex-column h-100"> */}
        {data?.ispopular && (
          <div className="popular-badge">
            <i className="fas fa-star me-2"></i>
            {t("popularBadge")}
          </div>
        )}
        <div className="membership-inner-card">
          <h5>{title}</h5>
          <p className="p-xs">{data.description}</p>
          <h4
            style={{
              fontSize: data.title === "Fit Enterprise" ? "1.6rem" : "",
            }}
          >
            {data.title === "Fit Enterprise" ? t("customPrice") : priceText}
          </h4>

          <p className="p-xs">{data.price_description}</p>
          <Button
            disabled={isDisabled || data?.button_disabled || isButtonLoading}
            text={isButtonLoading ? <Spinner size="sm" /> : finalButtonText}
            onClick={handleClick}
          />
        </div>
        <div className="mt-3 flex-grow-1">
          {data?.features.map((feature, i) => (
            <div className="d-flex" key={i}>
              <i className="fas fa-check mt-1" />
              <p className="ms-2">{feature}</p>
            </div>
          ))}
        </div>
        {/* </div> */}
      </div>

      <SwitchToBothRoleModal
        show={showRoleSwitchModal}
        close={() => setShowRoleSwitchModal(false)}
        onConfirm={handleSwitchRole}
        isLoading={isLoadingUpdate}
        title={t("switchRoleModal.title")}
        subtitle={t("switchRoleModal.subtitle")}
        paragraph1={t("switchRoleModal.paragraph1")}
        paragraph2={t("switchRoleModal.paragraph2")}
        roleText={t("switchRoleModal.role")}
        yesText={t("switchRoleModal.yesBtn")}
        cancelText={t("switchRoleModal.cancelBtn")}
      />
    </>
  );
};

export default memo(MembershipCard);
